/**
 * @file PlanDocumentCanvas.jsx
 * @module ui/src/components/tenant-admin/PlanDocumentCanvas
 * @description
 *   Routes between the eight plan-document sections and adapts the
 *   document object into each section's specific prop contract.
 *
 *   S810 REWRITE — OI-S810-SECTION-FORM-STATE
 *   - onFieldChange prop REMOVED. Each section owns its own useState form.
 *   - sectionRef forwarded to active section so parent can call getValues().
 *   - handleSave() calls sectionRef.current.getValues() → save(fields).
 *   - PlanWorkspace passes onSave={handleSave} to PlanWorkspaceHeader.
 *
 * @version 1.5.0
 * @since S755
 * @changelog
 *   1.0.0  S755  Initial build
 *   1.1.0  S807  Unpack document → section-specific props
 *   1.2.0  S807  Complete prop adapter for all 8 sections
 *   1.3.0  S810  Section-local form state rewrite — sectionRef + handleSave
 *   1.4.0  S815  OI-S814-CAP-DISPLAY-AFTER-MATERIALIZE — thread planComponent
 *                into PayoutAdjustmentsSection plan prop so initFromLayer2 can
 *                seed cap form from max_period_payout after materialization.
 *   1.5.0  S913  PCMA_MATRIX: 'mbo' added to all applicable spines (GA completeness).
 *                Excluded: bonus_pool only (discretionary by nature).
 */

import { forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';

import PlanIntelligencePanel       from './PlanIntelligencePanel';
import { PCMA_MATRIX }             from '../../constants/planConstants';
import PlanOverviewSection         from './sections/PlanOverviewSection';
import PlanIdentitySection         from './sections/PlanIdentitySection';
import CreditAllocationSection     from './sections/CreditAllocationSection';
import MeasurementEconomicsSection from './sections/MeasurementEconomicsSection';
import PayoutAdjustmentsSection    from './sections/PayoutAdjustmentsSection';
import SettlementSection           from './sections/SettlementSection';
// AssignmentScopeSection REMOVED — D3 locked S811. Assignment is the Assignments tab's responsibility.
import PlanGovernanceSection       from './sections/PlanGovernanceSection';

const fallbackStyle = {
  fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
  padding: '24px',
  color: 'rgb(var(--color-text-muted))',
  background: 'rgb(var(--color-surface))',
};

const noop = () => {};

/**
 * PlanDocumentCanvas
 *
 * Exposes handleSave() via ref so PlanWorkspace can wire the header Save
 * button → canvas.handleSave() → sectionRef.getValues() → save(fields).
 *
 * @param {object}   props
 * @param {string}   props.activeSection
 * @param {object}   props.document
 * @param {boolean}  props.amendmentMode
 * @param {boolean}  props.readOnly
 * @param {Function} props.save              — usePlanDocument.save(fields)
 * @param {Function} props.onSectionNavigate
 * @param {object}   props.validationErrors
 * @param {object}   ref                    — exposes { handleSave }
 */
const PlanDocumentCanvas = forwardRef(function PlanDocumentCanvas({
  activeSection,
  document,
  amendmentMode,
  readOnly,
  save,
  onSectionNavigate = noop,
  onOpenHistory = noop,
  onViewPopulation = noop,
  validationErrors,
  planId,
  planComponentId,
}, ref) {
  // sectionRef is forwarded to whichever section is currently active.
  // Each section exposes getValues() via useImperativeHandle.
  const sectionRef = useRef(null);

  // handleSave: called by PlanWorkspace when header Save button is pressed.
  // Calls the active section's getValues(), then fires one PATCH.
  // getValues() returns null to abort save (e.g. CreditAllocationSection when
  // non-individual mode has no layer0Type — sets inline error, returns null).
  // getValues() returns {} (empty object) when the active section has no
  // saveable fields (e.g. Overview, which is read-only derived state).
  useImperativeHandle(ref, () => ({
    // S972 OI-UI-11: handleSave is async — MeasurementEconomicsSection.getValues()
    // is now async (fires PATCH /plan-components/{id} for hybrid weights before
    // returning layer1Config). All other sections' getValues() are synchronous;
    // awaiting them is a no-op. Callers in PlanWorkspace must await handleSave().
    async handleSave() {
      const values = await sectionRef.current?.getValues?.();
      // null = section aborted save (validation failed — inline errors shown)
      if (values == null) return;
      // {} = no fields to save from this section (overview, governance)
      if (Object.keys(values).length === 0) return;
      save(values);
    },
  }), [save]);

  // ── Overview ───────────────────────────────────────────────────────────────
  if (activeSection === 'overview') {
    const planStatus = (document.plan ?? document.overview ?? {}).planStatus
                    ?? (document.plan ?? document.overview ?? {}).plan_status
                    ?? 'draft';

    if (planStatus === 'approved') {
      return (
        <PlanIntelligencePanel
          intelligence={document.intelligence ?? null}
          plan={document.plan ?? document.overview ?? {}}
          onViewPopulation={onViewPopulation}
        />
      );
    }

    return (
      <PlanOverviewSection
        plan={document.overview ?? document.plan ?? {}}
        narrative={document.narrative ?? { tier1Summary: null, tier2Constructs: [], tier3Coverage: null }}
        population={document.population ?? { count: 0, freshnessAt: new Date().toISOString(), status: 'stale' }}
        crlState={document.crlState ?? { recommendation: {}, currentConfig: {} }}
        readOnly={readOnly}
        onCRLApply={noop}
        onCRLDismiss={noop}
        onNavigate={onSectionNavigate}
        onViewPopulation={onViewPopulation}
      />
    );
  }

  // ── Identity ───────────────────────────────────────────────────────────────
  if (activeSection === 'identity') {
    return (
      <PlanIdentitySection
        ref={sectionRef}
        plan={document.plan ?? {}}
        roles={document.roles ?? []}
        segments={document.segments ?? []}
        fiscalPeriods={document.fiscalPeriods ?? []}
        readOnly={readOnly}
        validationErrors={validationErrors}
      />
    );
  }

  // ── Credit Allocation ──────────────────────────────────────────────────────
  if (activeSection === 'credit-allocation') {
    return (
      <CreditAllocationSection
        ref={sectionRef}
        plan={document.plan ?? {}}
        layer0Config={document.layer0Config ?? {
          creditingMode: 'individual', layer0Type: null,
          attributionRules: null, isActive: false,
        }}
        readOnly={readOnly}
        validationErrors={validationErrors}
      />
    );
  }

  // ── Measurement & Economics ────────────────────────────────────────────────
  // S902: planId + planComponentId threaded through to MeasurementEconomicsSection
  // so RateTableReference can call EP66 (create/clone rate table on draft component).
  // OI-S899-RATE-TABLE-CHANGE-UI-GAP fix — both props required for EP66 calls.
  if (activeSection === 'measurement-economics') {
    return (
      <MeasurementEconomicsSection
        ref={sectionRef}
        plan={document.plan ?? {}}
        layer1Config={document.layer1Config ?? {
          primarySpine: '', weights: {}, rateTableRef: {}, quotaSemantics: { measurementBasis: '', currencyCode: '' },
        }}
        readOnly={readOnly}
        validationErrors={validationErrors}
        planId={planId}
        planComponentId={planComponentId}
        planComponent={document.planComponent ?? null}
      />
    );
  }

  // ── Payout Adjustments ─────────────────────────────────────────────────────
  // PCMA_MATRIX imported from ui/src/constants/planConstants.js (S931 OI-S913-PCMA-MATRIX-DUPLICATE).
  // ep 2 correctly does NOT return pcmaMatrix — derived client-side from plan.primarySpine.
  // Server-supplied value takes precedence if present.
  const pcmaMatrix = (() => {
    const raw = document.pcmaMatrix;
    if (raw && Array.isArray(raw.availableModifiers) && raw.availableModifiers.length > 0) {
      return raw;
    }
    const spine = document.plan?.primarySpine ?? null;
    const available = PCMA_MATRIX[spine] ?? [];
    return { availableModifiers: available };
  })();

  if (activeSection === 'payout-adjustments') {
    // S815 OI-S814-CAP-DISPLAY-AFTER-MATERIALIZE:
    // Pass planComponent from EP2 document into the plan prop so
    // PayoutAdjustmentsSection.initFromLayer2 can seed the cap form
    // from max_period_payout after cap has materialized (draftConfig=null,
    // entityRefs populated). document.planComponent comes from usePlanDocument
    // normalizeRawPlan — it maps rawPlan.planComponent from EP2 response.
    const planWithComponent = {
      ...(document.plan ?? {}),
      planComponent: document.planComponent ?? null,
    };

    return (
      <PayoutAdjustmentsSection
        ref={sectionRef}
        plan={planWithComponent}
        layer2Config={document.layer2Config ?? { modifiers: [] }}
        pcmaMatrix={pcmaMatrix}
        readOnly={readOnly}
      />
    );
  }

  // ── Settlement ─────────────────────────────────────────────────────────────
  if (activeSection === 'settlement') {
    return (
      <SettlementSection
        ref={sectionRef}
        plan={document.plan ?? {}}
        settlementConfig={document.settlementConfig ?? {
          policy: 'none', trueUpFrequency: null, comparisonBasis: null,
          lookbackWindowMonths: null, carryForwardRules: null,
          yearEndTreatment: null, customTerms: null,
        }}
        readOnly={readOnly}
        validationErrors={validationErrors}
      />
    );
  }

  // ── Review & Governance ────────────────────────────────────────────────────
  if (activeSection === 'review-governance') {
    return (
      <PlanGovernanceSection
        plan={document.plan ?? {}}
        lineage={document.lineage ?? []}
        auditSummary={document.auditSummary ?? null}
        readOnly={readOnly}
        onOpenHistory={onOpenHistory}
      />
    );
  }

  return (
    <div style={fallbackStyle} role="status" data-component="PlanDocumentCanvasFallback">
      Section not found: {activeSection}
    </div>
  );
});

PlanDocumentCanvas.displayName = 'PlanDocumentCanvas';

PlanDocumentCanvas.propTypes = {
  activeSection:     PropTypes.string.isRequired,
  document:          PropTypes.object.isRequired,
  amendmentMode:     PropTypes.bool.isRequired,
  readOnly:          PropTypes.bool.isRequired,
  save:              PropTypes.func.isRequired,
  onSectionNavigate: PropTypes.func,
  onOpenHistory:     PropTypes.func,
  onViewPopulation:  PropTypes.func,
  validationErrors:  PropTypes.object.isRequired,
  planId:            PropTypes.number,
  planComponentId:   PropTypes.number,
};

export default PlanDocumentCanvas;
